import React from "react";
import { currencyFormatter } from "../../../../utils/currencyFormatter";
import { LabeledInput } from "../../../UI/LabeledInput";
import { CategoryTotals } from "../../../../types/budget/budget";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import {
  isCbsDefaultTaxPillVisible,
  isIbsDefaultTaxPillVisible,
  isIssDefaultTaxPillVisible,
} from "../../../../utils/appliedTaxesByCurrency";

type Props = {
  totals: CategoryTotals;
  showExtras: boolean;
  displayCurrencyCode?: string;
  showIofConverted?: boolean;
};

const COLUMNS = 5;

function chunkArray<T>(arr: T[], size: number): T[][] {
  return arr.reduce((acc: T[][], _, i) => {
    if (i % size === 0) acc.push(arr.slice(i, i + size));
    return acc;
  }, []);
}

function BaseValuesTotal({
  totals,
  showExtras,
  displayCurrencyCode,
  showIofConverted,
}: Props) {
  const isForeignCurrency =
    !!displayCurrencyCode && displayCurrencyCode !== "BRL";
  // Mesmo com a moeda de exibição em BRL, o agregado pode conter IOF vindo de
  // serviços em moeda estrangeira (ex.: bloco "Valores" do Fechamento, que usa
  // a moeda de câmbio global e não a de cada serviço) — por isso o campo
  // também aparece quando há IOF somado, não só quando a moeda é estrangeira.
  const hasIofValue =
    !!totals.iofTotals?.totalInBRL || !!totals.iofTotals?.totalInCurrency;
  const shouldShowIofConverted =
    showIofConverted ?? (isForeignCurrency || hasIofValue);
  const currencyCodeForTaxRules = displayCurrencyCode || "BRL";
  const shouldShowIssConverted = isIssDefaultTaxPillVisible(
    currencyCodeForTaxRules
  );
  const shouldShowCbsConverted = isCbsDefaultTaxPillVisible(
    currencyCodeForTaxRules
  );
  const shouldShowIbsConverted = isIbsDefaultTaxPillVisible(
    currencyCodeForTaxRules
  );

  const formatTotalsField = (_currencyValue: unknown, convertedValue: string) =>
    convertedValue;

  const fields: { label: string; value: string }[] = [
    {
      label: "Tarifas/Convertido",
      value: formatTotalsField(
        totals.tariffTotals.totalInCurrency,
        currencyFormatter.format(totals.tariffTotals.totalInBRL)
      ),
    },
    {
      label: "Taxa de Serviço/Convertido",
      value: formatTotalsField(
        totals.serviceTaxTotals.totalInCurrency,
        currencyFormatter.format(totals.serviceTaxTotals.totalInBRL)
      ),
    },
    ...(shouldShowIssConverted
      ? [
          {
            label: "ISS",
            value: formatTotalsField(
              totals.issTotals.totalInCurrency,
              currencyFormatter.format(totals.issTotals.totalInBRL)
            ),
          },
        ]
      : []),
    ...(shouldShowCbsConverted
      ? [
          {
            label: "CBS/Convertido",
            value: formatTotalsField(
              totals.cbsTotals?.totalInCurrency || 0,
              currencyFormatter.format(totals.cbsTotals?.totalInBRL || 0)
            ),
          },
        ]
      : []),
    ...(shouldShowIbsConverted
      ? [
          {
            label: "IBS/Convertido",
            value: formatTotalsField(
              totals.ibsTotals?.totalInCurrency || 0,
              currencyFormatter.format(totals.ibsTotals?.totalInBRL || 0)
            ),
          },
        ]
      : []),
    {
      label: "Imposto/Convertido",
      value: formatTotalsField(
        totals.tributeTotals.totalInCurrency,
        currencyFormatter.format(totals.tributeTotals.totalInBRL)
      ),
    },
    {
      label: "Imposto Bitributado/Convertido",
      value: formatTotalsField(
        totals.doubleChargedTotals.totalInCurrency,
        currencyFormatter.format(totals.doubleChargedTotals.totalInBRL)
      ),
    },
    {
      label: "Variável/Convertido",
      value: formatTotalsField(
        totals.localTaxTotals.totalInCurrency,
        currencyFormatter.format(totals.localTaxTotals.totalInBRL)
      ),
    },
    {
      label: "Taxa de Turismo/Convertido",
      value: formatTotalsField(
        totals.tourismTaxTotals.totalInCurrency,
        currencyFormatter.format(totals.tourismTaxTotals.totalInBRL)
      ),
    },
    {
      label: "Taxa MM/Convertido",
      value: formatTotalsField(
        totals.feeTotals.totalInCurrency,
        currencyFormatter.format(totals.feeTotals.totalInBRL)
      ),
    },
    ...(shouldShowIofConverted
      ? [
          {
            label: "IOF/Convertido",
            value: formatTotalsField(
              totals.iofTotals.totalInCurrency,
              currencyFormatter.format(totals.iofTotals.totalInBRL)
            ),
          },
        ]
      : []),
    {
      label: "Markup/Over/Convertido",
      value: formatTotalsField(
        totals.markupTotals.totalInCurrency,
        currencyFormatter.format(totals.markupTotals.totalInBRL)
      ),
    },
    {
      label: "Valor Fornecedor/Convertido",
      value: formatTotalsField(
        totals.supplierTributeTotals?.totalInCurrency || 0,
        currencyFormatter.format(totals.supplierTributeTotals?.totalInBRL || 0)
      ),
    },
    {
      label: "Comissão/Convertido",
      value: formatTotalsField(
        totals.commissionTotals.totalInCurrency,
        currencyFormatter.format(totals.commissionTotals.totalInBRL)
      ),
    },
    {
      label: "Incentivo/Convertido",
      value: formatTotalsField(
        totals.incentiveTotals.totalInCurrency,
        currencyFormatter.format(totals.incentiveTotals.totalInBRL)
      ),
    },
    {
      label: "Geral/Convertido",
      value: formatTotalsField(
        totals.totals.totalInCurrency,
        currencyFormatter.format(totals.totals.totalInBRL)
      ),
    },
    ...(showExtras
      ? [
          {
            label: "Previsão de extras/Convertido",
            value: formatTotalsField(
              totals.extraTotals?.totalInCurrency || 0,
              currencyFormatter.format(totals.extraTotals?.totalInBRL || 0)
            ),
          },
          {
            label: "Total + Previsão de extras/Convertido",
            value: formatTotalsField(
              totals.totals.totalInCurrency +
                (totals.extraTotals?.totalInCurrency || 0),
              currencyFormatter.format(
                totals.totals.totalInBRL + (totals.extraTotals?.totalInBRL || 0)
              )
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          border: "1px solid #E3E3E3",
          padding: 16,
          borderRadius: 8,
          marginTop: 8,
        }}
      >
        <span style={{ fontWeight: 600, marginBottom: 8 }}>Total</span>
        {chunkArray(fields, COLUMNS).map((group, groupIndex) => (
          <Box
            key={groupIndex}
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              mb: groupIndex < chunkArray(fields, COLUMNS).length - 1 ? 2 : 0,
            }}
          >
            {group.map((field, index) => (
              <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
                <LabeledInput
                  disabled
                  label={field.label}
                  value={field.value}
                />
              </Box>
            ))}
            {Array.from({ length: COLUMNS - group.length }).map((_, i) => (
              <Box key={`filler-${i}`} sx={{ flex: 1, minWidth: 0 }} />
            ))}
          </Box>
        ))}
      </div>
    </>
  );
}

export default BaseValuesTotal;
